<template>
    <transition name="modal-fade">
        <div v-if="isOpen" class="file-preview-overlay">
            
            <div class="preview-header">
                <button type="button" class="btn-icon-close" @click="closeModal" title="Fechar">
                    <i class="ri-close-line"></i>
                </button>
                <h5 class="preview-title text-truncate">{{ currentFileName }}</h5>

                <div class="header-actions">
                    <button
                        v-if="isCurrentImage"
                        type="button"
                        class="btn-icon-edit"
                        title="Editar imagem"
                        @click="openEditor"
                    >
                        <i class="ri-edit-2-line"></i>
                    </button>
                </div>
            </div>

            <div class="preview-body" @click="isEmojiOpen = false">
                <transition name="zoom-fade" mode="out-in">
                    
                    <div v-if="isCurrentImage" :key="currentPreviewUrl" class="image-container">
                        <img :src="currentPreviewUrl" class="preview-img">
                    </div>

                    <div v-else-if="isCurrentVideo" :key="'vid-'+currentPreviewUrl" class="image-container">
                        <video v-liberar-midia 
                            :src="currentPreviewUrl" 
                            class="preview-img" 
                            controls 
                            autoplay 
                            muted
                            style="border-radius: 8px; box-shadow: 0 4px 10px rgba(0,0,0,0.15);"
                        ></video>
                    </div>
                    
                    <div v-else :key="'file-'+currentFileName" class="file-generic-container">
                        <div class="file-card-large">
                            <i class="ri-file-text-fill file-icon-large"></i>
                            <div class="file-info-large" style="max-width: 100%;">
                                <p class="mb-1 text-truncate file-name-large">{{ currentFileName }}</p>
                                <small class="text-muted">{{ currentFileSize }} • {{ currentFileExt }}</small>
                            </div>
                        </div>
                        <p class="text-center no-preview-text mt-4">Nenhuma prévia disponível</p>
                    </div>

                </transition>
            </div>

            <transition name="slide-up">
                <div 
                    v-if="isEmojiOpen" 
                    class="emoji-picker-container"
                    v-click-outside="closeEmoji" 
                >
                    <EmojiPickerField 
                        :theme="isDarkMode ? 'dark' : 'light'" 
                        @select="onSelectEmoji"
                    />
                </div>
            </transition>

            <div class="preview-footer">

                <div class="caption-bar">
                    <!-- Áudio não tem legenda no WhatsApp (o proto do audioMessage não tem
                         `caption`) e o envio descarta o texto. A caixa some para não
                         prometer o que não vai ser entregue. No chat interno a legenda
                         de áudio é do próprio Whiz e continua valendo. -->
                    <div v-if="audioSemLegenda" class="caption-sem-legenda">
                        <i class="ri-information-line"></i>
                        <span>Áudio não aceita legenda no WhatsApp.</span>
                    </div>
                    <div v-else class="input-wrapper" :class="{ 'focused': isInputFocused }">

                        <div v-if="showMentionList && (isLoadingParticipants || filteredParticipants.length > 0)" class="caption-mention-dropdown">
                            <div v-if="isLoadingParticipants" class="caption-mention-loading">
                                <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
                                <span>Carregando membros...</span>
                            </div>
                            <template v-else>
                                <div
                                    v-for="(participant, index) in filteredParticipants"
                                    :key="participant.phone"
                                    class="caption-mention-item"
                                    :class="{ active: index === selectedMentionIndex }"
                                    @mousedown.prevent="selectParticipant(participant)"
                                >
                                    <div class="caption-mention-avatar">{{ participant.name.substring(0, 2).toUpperCase() }}</div>
                                    <div class="caption-mention-info">
                                        <span class="caption-mention-name">{{ participant.name }}</span>
                                        <span v-if="participant.phone" class="caption-mention-phone">{{ participant.phone }}</span>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <button
                            class="btn-icon-emoji"
                            :class="{ 'active': isEmojiOpen }"
                            @click.stop="toggleEmoji"
                            title="Emojis"
                        >
                            <i class="ri-emotion-happy-line"></i>
                        </button>

                        <textarea
                            ref="captionInputRef"
                            class="caption-input"
                            placeholder="Adicione uma legenda..."
                            v-model="currentItem.caption"
                            @focus="isInputFocused = true"
                            @blur="handleCaptionBlur"
                            @keydown="handleKeydown"
                            @input="checkMentionTrigger"
                            @paste="handleCaptionPaste"
                            rows="1"
                        ></textarea>
                    </div>
                    
                    <button class="btn-send-fab" @click="handleSendAll" title="Enviar Tudo">
                        <i class="ri-send-plane-fill"></i>
                    </button>
                </div>
                
                <div class="thumbnails-strip mt-3">
                    <div 
                        v-for="(item, index) in previewItems" 
                        :key="index"
                        class="thumb-item" 
                        :class="{ active: index === activeIndex }"
                        @click="uiStore.setActivePreviewIndex(index)"
                        :title="item.file.name"
                    >
                        <button class="btn-remove-thumb" @click.stop="removeItem(index)" title="Remover arquivo">
                            <i class="ri-close-line"></i>
                        </button>

                        <img v-if="item.file.type.startsWith('image/')" :src="getThumbUrl(item.file)">
                        <div v-else class="thumb-file-icon"><i class="ri-file-text-fill"></i></div>
                    </div>

                    <button class="thumb-add-btn" @click="triggerAddFile" title="Adicionar mais arquivos">
                        <i class="ri-add-line"></i>
                    </button>
                    <input type="file" ref="addFileInput" class="d-none" @change="handleNewFile" multiple>
                </div>

            </div>

            <ImageEditorModal
                :is-open="isEditorOpen"
                :image-file="currentFile"
                @save="onEditSave"
                @close="isEditorOpen = false"
            />

        </div>
    </transition>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { useUiStore } from '@/stores/app/uiStore';
import { useChatStore } from '@/stores/app/chatStore';
import { MakeChat } from '@/stores/app/chatUtils';
import EmojiPickerField from '@/components/forms/EmojiPickerField.vue';
import ImageEditorModal from '@/components/app/modals/ImageEditorModal.vue';
import { ehAnexoDeAudio } from '@/utils/anexoAudio';

const handleKeydown = (e) => {
    if (showMentionList.value && filteredParticipants.value.length > 0) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            selectedMentionIndex.value = (selectedMentionIndex.value + 1) % filteredParticipants.value.length;
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            selectedMentionIndex.value = (selectedMentionIndex.value - 1 + filteredParticipants.value.length) % filteredParticipants.value.length;
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            selectParticipant(filteredParticipants.value[selectedMentionIndex.value]);
        } else if (e.key === 'Escape') {
            showMentionList.value = false;
        }
    } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendAll();
    }
};

const autoGrow = () => {
    const el = captionInputRef.value;
    if (!el) return;
    
    const maxHeight = 120; // Limite máximo de altura antes de criar scroll
    el.style.height = 'auto';
    
    if (el.scrollHeight > maxHeight) {
        el.style.height = maxHeight + 'px';
        el.style.overflowY = 'auto';
    } else {
        el.style.height = (el.scrollHeight) + 'px'; 
        el.style.overflowY = 'hidden';
    }
};

// Diretiva local Click Outside
const vClickOutside = {
    mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
            if (!(el === event.target || el.contains(event.target))) {
                binding.value(event, el);
            }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
    },
    unmounted(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
    }
};

const uiStore = useUiStore();
const chatStore = useChatStore();
import { useInternalChatStore } from '@/stores/app/chatInternoStore';
const internalChatStore = useInternalChatStore();

const isEmojiOpen = ref(false);
const isEditorOpen = ref(false);
const isInputFocused = ref(false);
const captionInputRef = ref(null);
const addFileInput = ref(null);

// Mention state
const showMentionList = ref(false);
const mentionQuery = ref('');
const selectedMentionIndex = ref(0);
const cursorPositionAtMentionStart = ref(null);
const mentioned = ref([]);
const isLoadingParticipants = ref(false);

const filteredParticipants = computed(() => {
    if (!chatStore.activeChat?.isGroup || !chatStore.activeChat?.participants) return [];
    const query = mentionQuery.value.toLowerCase();
    return chatStore.activeChat.participants.filter(p => {
        // Mesmo predicado do autocomplete do campo de mensagem: sem ele, digitar `@`
        // aqui listava justamente quem o outro já excluía.
        if (!MakeChat.participantePodeSerMencionado(p)) return false;

        const matchName = (p.name ?? '').toLowerCase().includes(query);
        const matchPhone = p.phone ? p.phone.includes(query) : false;
        return matchName || matchPhone;
    });
});

const fetchParticipantsIfNeeded = async () => {
    if (!chatStore.activeChat || isLoadingParticipants.value) return;
    if (!MakeChat.participantsNeedRefresh(chatStore.activeChat)) return; // Evita recarregar se os participantes já estão atualizados
    isLoadingParticipants.value = true;
    await MakeChat.FetchAndPopulateWithParticipants(chatStore.activeChat);
    isLoadingParticipants.value = false;
};

const handleCaptionBlur = () => {
    isInputFocused.value = false;
    setTimeout(() => { showMentionList.value = false; }, 200);
};

const checkMentionTrigger = (event) => {
    autoGrow();
    if (!chatStore.activeChat?.isGroup) return;

    const el = event.target;
    const cursor = el.selectionStart;
    const text = el.value;
    const textBeforeCursor = text.slice(0, cursor);
    const match = textBeforeCursor.match(/(?:\s|^)@(\w*)$/);

    if (match) {
        showMentionList.value = true;
        mentionQuery.value = match[1];
        const matchIndex = match.index !== undefined ? match.index : 0;
        cursorPositionAtMentionStart.value = textBeforeCursor.endsWith(match[0])
            ? matchIndex + (match[0].startsWith(' ') ? 1 : 0)
            : cursor - match[0].length;
        selectedMentionIndex.value = 0;
        fetchParticipantsIfNeeded();
    } else {
        showMentionList.value = false;
        cursorPositionAtMentionStart.value = null;
    }
};

const selectParticipant = (participant) => {
    if (cursorPositionAtMentionStart.value === null || !captionInputRef.value) return;

    const originalText = currentItem.value.caption || '';
    const beforeMention = originalText.slice(0, cursorPositionAtMentionStart.value);
    const currentCursor = captionInputRef.value.selectionStart;
    const afterCursor = originalText.slice(currentCursor);
    const mentionText = `@${participant.name} `;

    currentItem.value.caption = beforeMention + mentionText + afterCursor;
    showMentionList.value = false;

    nextTick(() => {
        if (captionInputRef.value) {
            captionInputRef.value.focus();
            const newCursorPos = beforeMention.length + mentionText.length;
            captionInputRef.value.selectionStart = newCursorPos;
            captionInputRef.value.selectionEnd = newCursorPos;
        }
    });

    if (!mentioned.value.find(p => p.phone === participant.phone)) {
        mentioned.value.push(participant);
    }
};

const urlCache = new Map();

// --- COMPUTEDS ---
const isOpen = computed(() => uiStore.isFilePreviewModalOpen);
const previewItems = computed(() => uiStore.previewItems);
const activeIndex = computed(() => uiStore.activePreviewIndex);
const isDarkMode = computed(() => uiStore.theme === 'dark');

const currentItem = computed(() => {
    if (previewItems.value.length > 0 && previewItems.value[activeIndex.value]) {
        return previewItems.value[activeIndex.value];
    }
    return { file: null, caption: '' };
});

const currentFile = computed(() => currentItem.value.file);
const currentFileName = computed(() => currentFile.value?.name || '');
const currentFileSize = computed(() => currentFile.value ? (currentFile.value.size / 1024 / 1024).toFixed(2) + ' MB' : '');
const currentFileExt = computed(() => currentFileName.value.split('.').pop().toUpperCase());
const isCurrentImage = computed(() => currentFile.value && currentFile.value.type.startsWith('image/'));
const isCurrentVideo = computed(() => currentFile.value && currentFile.value.type.startsWith('video/'));
// Mesmo predicado que o envio usa para escolher `enviarAudio` — se divergir, a
// caixa de legenda volta a aparecer para um arquivo cuja legenda é descartada.
const ehAudio = (file) => !!file && ehAnexoDeAudio(file.name || '', file.type || '');
// Só o WhatsApp não tem legenda de áudio; o chat interno (`team`) é renderizado
// pelo próprio Whiz e guarda a legenda na mensagem.
const semLegendaDeAudio = computed(() => chatStore.chatContext !== 'team');
const audioSemLegenda = computed(() => semLegendaDeAudio.value && ehAudio(currentFile.value));

const currentPreviewUrl = computed(() => {
    if (!currentFile.value) return '';
    return getThumbUrl(currentFile.value);
});

// --- MÉTODOS ---

const getThumbUrl = (file) => {
    if (!urlCache.has(file)) {
        const url = URL.createObjectURL(file);
        urlCache.set(file, url);
    }
    return urlCache.get(file);
};

const closeModal = () => {
    uiStore.closeFilePreviewModal();
    isEmojiOpen.value = false;
    showMentionList.value = false;
    mentioned.value = [];
    isLoadingParticipants.value = false;
    urlCache.forEach(url => URL.revokeObjectURL(url));
    urlCache.clear();
};

const closeEmoji = () => {
    if (isEmojiOpen.value) isEmojiOpen.value = false;
};

const toggleEmoji = () => {
    isEmojiOpen.value = !isEmojiOpen.value;
};

// Remove item da lista ao clicar no X
const removeItem = (index) => {
    uiStore.removeFileFromPreview(index);
};

/**
 * Cada arquivo falha por conta própria: a rejeição de um envio não pode abortar
 * o lote (os demais nunca eram enviados e o modal ficava aberto). O balão de erro
 * já é exibido no chat pelo `handleSendMessage`, com opção de reenviar.
 */
const enviarArquivo = (file, caption, mentioned = [], skipSignature = false) =>
    chatStore.sendFileMessage(file, caption, mentioned, skipSignature).catch(() => { /* erro já sinalizado no balão */ });

const handleSendAll = async () => {
    // Áudio vai sem legenda: o campo não existe no proto do WhatsApp e o envio a
    // descarta. Zerar aqui evita que a bolha otimista mostre um texto que nunca
    // foi enviado (o operador pode ter digitado antes de trocar de anexo).
    if (semLegendaDeAudio.value) {
        previewItems.value.forEach(item => {
            if (ehAudio(item.file)) item.caption = '';
        });
    }

    // Process mentions: filter to those still in caption, normalize phone
    const activeMentioned = mentioned.value
        .filter(p => (currentItem.value.caption || '').includes(`@${p.name}`))
        .map(p => ({ ...p, phone: p.phone.replace(/^(55\d{2})9(\d{8})$/, '$1$2') }));

    const items = previewItems.value;

    if (chatStore.chatContext === 'team') {
        items.forEach(item => {
            internalChatStore.sendFileInternalMessage(item.file, item.caption, item.size);
        });
    } else if (items.length > 1 && chatStore.isSignatureEnabled) {
        // Aguarda o primeiro arquivo (que inclui a assinatura) ser enviado antes dos demais,
        // garantindo que a assinatura apareça antes de todos os arquivos na conversa
        await enviarArquivo(items[0].file, items[0].caption);
        items.slice(1).forEach(item => enviarArquivo(item.file, item.caption, [], true));
    } else {
        items.forEach(item => {
            let caption = item.caption || '';
            activeMentioned.forEach(p => {
                caption = caption.replaceAll(`@${p.name}`, `@${p.phone}`);
            });
            enviarArquivo(item.file, caption, activeMentioned);
        });
    }

    closeModal();
};

const onSelectEmoji = (emoji) => {
    const input = captionInputRef.value;
    if (input) {
        const val = currentItem.value.caption || '';
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const before = val.substring(0, start);
        const after = val.substring(end);
        currentItem.value.caption = before + emoji.i + after;
        
        nextTick(() => {
            input.selectionStart = input.selectionEnd = start + emoji.i.length;
            input.focus();
        });
    } else {
        currentItem.value.caption += emoji.i;
    }
};

const triggerAddFile = () => {
    if (addFileInput.value) addFileInput.value.value = '';
    addFileInput.value?.click();
};

const handleNewFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
        uiStore.addFileToPreview(Array.from(e.target.files));
    }
};

const openEditor = () => {
    if (!isCurrentImage.value) return;
    isEditorOpen.value = true;
};

const onEditSave = (editedFile) => {
    const oldFile = currentFile.value;
    uiStore.replaceFileInPreview(activeIndex.value, editedFile);

    // Invalida o objectURL antigo para que o preview/thumb reflitam a imagem editada
    if (oldFile && urlCache.has(oldFile)) {
        URL.revokeObjectURL(urlCache.get(oldFile));
        urlCache.delete(oldFile);
    }

    isEditorOpen.value = false;
};

const focusCaption = () => {
    nextTick(() => {
        captionInputRef.value?.focus();
        autoGrow();
    });
};

/**
 * Ctrl+V no campo de legenda: adiciona o print aos anexos e passa a editá-lo.
 * Sem isto o foco continuava no textarea do chat (atrás do modal), que capturava
 * o paste e reposicionava a seleção no primeiro arquivo.
 */
const handleCaptionPaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of items) {
        if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (file) files.push(file);
    }

    if (files.length === 0) return; // texto puro segue o paste nativo

    event.preventDefault();
    // `addFileToPreview` já deixa o último arquivo adicionado como ativo.
    uiStore.addFileToPreview(files);
    focusCaption();
};

watch(activeIndex, focusCaption);

watch(isOpen, (aberto) => {
    if (aberto) focusCaption();
});

onUnmounted(() => {
    urlCache.forEach(url => URL.revokeObjectURL(url));
    urlCache.clear();
});
</script>

<style scoped>
/* === LAYOUT PRINCIPAL === */
.file-preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 50;
    display: flex;
    flex-direction: column;
    /* 🚀 COR DO SISTEMA (Fundo Terciário: cinza claro ou escuro conforme tema) */
    background-color: var(--bs-light); 
    user-select: none;
}

/* === HEADER === */
.preview-header {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    background-color: transparent;
    z-index: 10;
    height: 64px;
}

.btn-icon-close {
    background: transparent;
    border: none;
    font-size: 24px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    /* 🚀 COR DO SISTEMA (Texto secundário) */
    color: var(--bs-secondary-color);
    transition: background 0.2s, color 0.2s;
    margin-right: 16px;
}
.btn-icon-close:hover {
    background-color: var(--bs-border-color); /* Cor de borda suave */
    color: var(--bs-body-color); /* Cor de texto principal */
}

.preview-title {
    font-size: 16px;
    font-weight: 500;
    color: var(--bs-text-muted);
    margin: 0;
    flex-grow: 1;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 16px;
}

.btn-icon-edit {
    background: transparent;
    border: none;
    font-size: 22px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--bs-secondary-color);
    transition: background 0.2s, color 0.2s;
    cursor: pointer;
}
.btn-icon-edit:hover {
    background-color: var(--bs-border-color);
    color: var(--bs-body-color);
}

/* === BODY === */
.preview-body {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    background-color: transparent;
}

.image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;
}

.preview-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 4px 10px rgba(0,0,0,0.15));
    border-radius: 8px;
}

/* Card de Arquivo Genérico */
.file-generic-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: float 3s ease-in-out infinite;
}
.file-card-large {
    background-color: var(--bs-white);
    border-radius: 12px;
    padding: 32px;
    width: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    /* Sombra suave usando variável se possível, ou fallback */
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}

.file-icon-large { font-size: 56px; color: var(--bs-danger); margin-bottom: 16px; }
.file-name-large { font-weight: 600; color: var(--bs-body-color); font-size: 15px; max-width: 100%; margin-bottom: 4px; }
.no-preview-text { color: var(--bs-secondary-color); font-size: 14px; }

/* === EMOJI PICKER === */
.emoji-picker-container {
    position: absolute;
    bottom: 80px;
    left: 20px;
    z-index: 100;
}

/* === FOOTER === */
.preview-footer {
    padding: 12px 0 24px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 20;
    background: transparent;
}

.caption-sem-legenda {
    flex: 1;
    display: flex;
    align-items: center;
    gap: .5rem;
    padding: .65rem 1rem;
    border-radius: 24px;
    font-size: .875rem;
    color: var(--bs-secondary-color);
    background-color: var(--bs-tertiary-bg);
}

.caption-bar {
    width: 100%;
    max-width: 700px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 16px;
}

/* Wrapper do Input (Pílula) */
.input-wrapper {
    position: relative;
    flex-grow: 1;
    /* 🚀 COR DO SISTEMA (Fundo do Input = Fundo do Body ou Branco) */
    background-color: var(--bs-white);
    border-radius: 26px;
    padding: 4px 6px;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.input-wrapper.focused {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.caption-input {
    border: none;
    background: transparent;
    flex-grow: 1;
    padding: 10px;
    font-size: 15px;
    color: var(--bs-body-color);

    resize: none;
    overflow-y: hidden;
    min-height: 44px;
    font-family: inherit;
}
.caption-input:focus { outline: none; }
.caption-input::placeholder { color: var(--bs-secondary-color); }

/* Botão Emoji */
.btn-icon-emoji {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: var(--bs-secondary-color);
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    cursor: pointer;
}
.btn-icon-emoji:hover, .btn-icon-emoji.active { color: var(--bs-body-color); }

/* Botão Enviar */
.btn-send-fab {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: #00a884; /* Mantido verde WhatsApp */
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.15);
    transition: transform 0.2s, background-color 0.2s;
    cursor: pointer;
}
.btn-send-fab:hover {
    background-color: #008f6f;
    transform: scale(1.05);
}

/* === THUMBNAILS === */
.thumbnails-strip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 12px;
    overflow-x: auto;
    max-width: 100%;
    scrollbar-width: none;
}
.thumbnails-strip::-webkit-scrollbar { display: none; }

.thumb-item {
    width: 52px;
    height: 52px;
    border-radius: 8px;
    overflow: visible;
    border: 2px solid #00a88400;
    cursor: pointer;
    position: relative;
    background-color: var(--bs-white);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s;
    flex-shrink: 0;
}

.thumb-item:hover { transform: translateY(-2px); }
.thumb-item.active { border-color: #00a884; }
.thumb-item img { width: 100%; height: 100%; object-fit: cover; border-radius: 6px; }
.thumb-file-icon { font-size: 24px; color: var(--bs-danger); }

.btn-remove-thumb {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 18px;
    height: 18px;
    background-color: #cfdce4; /* Cor cinza claro */
    color: #54656f;
    border: none;
    border-radius: 50%;
    display: none; /* Oculto por padrão */
    align-items: center;
    justify-content: center;
    font-size: 12px;
    cursor: pointer;
    z-index: 5;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.thumb-item:hover .btn-remove-thumb {
    display: flex; /* Aparece no hover */
}
.btn-remove-thumb:hover {
    background-color: #fff;
    color: #d32f2f; /* Vermelho ao passar mouse no X */
}

/* Botão + */
.thumb-add-btn {
    width: 52px;
    height: 52px;
    border-radius: 8px;
    border: 1px solid var(--bs-border-color);
    background-color: var(--bs-white);
    color: var(--bs-secondary-color);
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}
.thumb-add-btn:hover {
    background-color: rgba(var(--bs-primary-rgb), 0.08);
    color: var(--bs-body-color);
}

/* === MENTION DROPDOWN === */
.caption-mention-dropdown {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    margin-bottom: 6px;
    background-color: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 8px;
    max-height: 200px;
    overflow-y: auto;
    box-shadow: 0 -4px 12px rgba(0,0,0,0.15);
    z-index: 10;
}
.caption-mention-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.15s;
}
.caption-mention-item:hover,
.caption-mention-item.active {
    background-color: var(--bs-secondary-bg);
}
.caption-mention-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: var(--bs-success);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
}
.caption-mention-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.caption-mention-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    font-size: 13px;
    color: var(--bs-secondary-color);
}
.caption-mention-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--bs-body-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.caption-mention-phone {
    font-size: 11px;
    color: var(--bs-secondary-color);
}

/* === ANIMAÇÕES === */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

.zoom-fade-enter-active, .zoom-fade-leave-active { transition: all 0.3s ease; }
.zoom-fade-enter-from, .zoom-fade-leave-to { opacity: 0; transform: scale(0.95); }

.slide-up-enter-active, .slide-up-leave-active { transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(20px); }

@keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0px); }
}


</style>